/*eslint no-mixed-operators: "off"*/
/*global ExecutionRuntimeModule, BlocklyPromises*/
import postRobot from 'post-robot';

import {
    looksLikeXml,
    xmlToJson,
    xmlSetProperty,
    xmlAddChild,
    xmlElementToJson,
} from './xmlUtils';

const serverURL = import.meta.env.VITE_DASHBOARD_API_ROOT;

// enum for getting app type (used for filtering blocks)
export const AppType = {
    STANDARD: 1,
    WORKFLOW: 2,
    LOCKDOWN: 3
}

// Convert a space seperated string to an upper snake cased string
// e.g. "cat and dog" -> "CAT_AND_DOG"
export function upperSnakeCase (string) {
    return string.replaceAll(' ', '_').toUpperCase();
}

// Convert an underscore or space sepertated sting to a camel-cased string
// e.g. "eat RHUBARB_pie" -> "eatRhubarbPie"
export function camelCase (string) {
    let words = string.toLowerCase().split(/[_ ]/);
    return words.reduce((word, next) =>
        word + next[0].toUpperCase() + next.slice(1));
}

/**
 * parseToJsonPromise
 * Returns JSON result for the fetch response
 * e.g. parseToJsonPromise(data) => { ...values }
 *
 **/

function parseToJsonPromise (data) {
    return new Promise(res => {
        data.json().then(object => {
            let response = {
                successful: [200, 201].includes(data.status),
                data: object,
                code: data.status
            };

            return res(response);
        }).catch(function(error) {
            // This typically happens when the response has no body (empty stream),
            // which can occur if the Blockly iframe is closed before the response finishes.
            // Instead of treating this as a critical error, we log a warning and allow the caller
            // to handle the incomplete or aborted response gracefully.
            console.warn("Error while reading response JSON", error, data);
        })
    });
}

const apiGenerator = (uri, method, payload, type) => {
    const token = localStorage.getItem("auth_token");

    let headers = {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
    };

    if (type !== "file") {
        headers["Content-Type"] = "application/json";
        payload = JSON.stringify(payload);
    }
    console.log(serverURL)
    return fetch(`${serverURL}${uri}`, {
        method,
        body: payload,
        headers
    }).then(parseToJsonPromise).catch(error => { throw error });
};

const networkAdapter = (generator) => {
    return {
        POST: (url, payload, type) => generator(url, 'POST', payload, type),
        PUT: (url, payload) => generator(url, 'PUT', payload),
        DELETE: (url, payload) => generator(url, 'DELETE', payload),
        GET: (url, token, payload) => generator(url, 'GET', payload)
    }
};

const bridgeAdapter = (generator) => {
    return {
        send: (type, payload, timeout) => generator(type, payload, timeout),
    }
};

const bridgeGenerator = (uri, payload) => {
    return new Promise((res) => {
        try {
            postRobot.send(window.parent, uri, payload)
                .then(({ data }) => {
                    res({ successful: true, data })
                }).catch(e => {
                res({ successful: false, error: e })
            })
        } catch (e) {
            res({ successful: false, error: e })
        }
    });
};

// The functions below are embedded verbatim (via Function.prototype.toString)
// into the generated device runtime code by blocklyPrefixFunctions /
// blocklyPostfixFunctions (see blocklyPrefix / blocklyPostfix exports below).
// They are exported here, as standalone top-level functions, so their logic
// can be unit tested directly by calling them, in addition to being exercised
// at runtime via eval in the generated Blockly code.
export function toNumber(a, defaultVal){
    let r;
    if (typeof(a)=='number'){
        r = a;
    } else if (typeof(a)=='string'){
        r = Number.parseFloat(a);
        if (Number.isNaN(r)) {
            r = defaultVal;
        }
    } else {
        r = defaultVal;
    }
    return r;
}

export function meanOfNumbers(list) {
    list = list.filter(function(x){return typeof(x)=='number';});
    if (!list.length) return null;
    return list.reduce(function(x,y){return x+y;}, 0) / list.length;
}

export function medianOfNumbers(list) {
    const localList = list.filter(function(x){return typeof x == 'number';});

    if (!localList.length) return null;
    localList.sort(function(a, b) {return b - a;});

    if (localList.length % 2 === 0) {
        return (localList[localList.length / 2 - 1] + localList[localList.length / 2]) / 2;
    } else {
        return localList[(localList.length - 1) / 2];
    }
}

export function getDateField(field, date) {
    let timestamp = new Date();
    if (date) { 
        timestamp = date?.getMonth ? date : new Date(date); 
    }

    switch (field) {
        case 'date':
            return timestamp.getDate();
        case 'month':
            return timestamp.getMonth()+1;
        case 'year':
            return timestamp.getFullYear();
    }
}

export function getTimeField(field, time) {
    let timestamp = new Date();
    if (time) {
        if (time.getHours) {
            timestamp = time;
        } else {
            const hour = Math.floor(time / 3600);
            const minute = Math.floor((time - hour * 3600) / 60);
            const second = time - hour * 3600 - minute * 60;
            timestamp.setHours(hour, minute, second);
        }
    }

    switch (field) {
        case 'hour':
            return timestamp.getHours();
        case 'minute':
            return timestamp.getMinutes();
        case 'second':
            return timestamp.getSeconds();
    }
}

export function convertTimeToString(datetime) {
    datetime.setFullYear(2020,1,1);
    const hour = datetime.getHours();
    const minute = datetime.getMinutes();
    const second = datetime.getSeconds();

    const tosec = ((hour * 60) + minute) * 60 + second;
    return tosec.toString();
}

export function convertStringToTime(timeString) {
    const sec = Number.parseInt(timeString, 10);
    const hour = Math.floor(sec / 3600);
    const minute = Math.floor((sec - hour * 3600) / 60);
    const second = sec - hour * 3600 - minute * 60;

    const d = new Date();
    d.setHours(hour, minute, second);

    d.setFullYear(2020,1,1);
    return d;
}

export function numberPad(n, width, z = '0') {
    n = n + '';
    return n.padStart(width, z);
}

export function createDateAsUTC(date) {
    if (typeof date === "string") {
        date = new Date(date);
    }
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0));
}

export function isJSON(str) {
    try {
        JSON.parse(str);
    } catch (e) {
        return false;
    }
    return true;
}

export function executeBlocklyPromises() {
    BlocklyPromises.length && ExecutionRuntimeModule.Execute(BlocklyPromises)
}

const blocklyPrefixFunctions = function () {
    const funcs = {
        toNumber,
        meanOfNumbers,
        medianOfNumbers,
        getDateField,
        getTimeField,
        convertTimeToString,
        convertStringToTime,
        numberPad,
        createDateAsUTC,
        isJSON,
        looksLikeXml,
        xmlToJson,
        xmlSetProperty,
        xmlAddChild,
        xmlElementToJson,
        BlocklyTmps:'{}', // For intermediate variables, so blockly vars don't conflict. eg: BlocklTmps[<blockId>] = some intermediate calculation
        BlocklyPromises: '[]',
        ResponseScope: '{}' // For storing async responses by block ID
    };

    return Object.keys(funcs).reduce((stringifiedFuncs, funcName) => {
        return `${stringifiedFuncs}\nvar ${funcName} = ${funcs[funcName].toString()};`;
    }, "");
};

const blocklyPostfixFunctions = function () {
    const funcs = {
        executeBlocklyPromises
    };

    let postFuncs = Object.keys(funcs).reduce((stringifiedFuncs, funcName) => {
        return `${stringifiedFuncs}\nvar ${funcName} = ${funcs[funcName].toString()};`;
    }, "");

    postFuncs += "\nexecuteBlocklyPromises()";
    return postFuncs;
};


// returns true if bounding boxes rectA and rectB intersect
export function doDomNodesIntersect(domA, domB) {
    if (!domA || !domB) return false;
    const rectA = domA.getBoundingClientRect();
    const rectB = domB.getBoundingClientRect();
    const maxLeft = Math.max(rectA.left, rectB.left);
    const minRight = Math.min(rectA.right, rectB.right);
    const maxTop = Math.max(rectA.top, rectB.top);
    const minBottom = Math.min(rectA.bottom, rectB.bottom);
    return (maxLeft < minRight && maxTop < minBottom);
}

export function blockToXmlSanitized (block) {
    if (block.getElementsByTagName("block").length > 0) {
        for (let i = block.getElementsByTagName("block")[0].children.length - 1; i >= 0; i--) {
            let child = block.getElementsByTagName("block")[0].children.item(i);
            if (child.tagName === "value") {
                child.remove();
            }
        }
    }
    for (let i = block.getElementsByTagName("variables").length - 1; i >= 0; i--) {
        let variable = block.getElementsByTagName("variables")[i];
        variable.remove();
    }
    return block;
}

export function encodeHTML (html) {
    return html.replaceAll(/[\u00A0-\u9999<>&]/gim, i => `&#${i.codePointAt(0)};`);
}

// Add tenant identifiers to payload object
// Only includes tenantId if it's a real value (not default -1)
// Always includes snapTenantGuid if available
export function addTenantToPayload(payload, tenantId, snapTenantGuid) {
    if (tenantId && tenantId !== "-1" && tenantId !== -1) {
        payload.tenantId = tenantId;
    }
    if (snapTenantGuid) {
        payload.snapTenantGuid = snapTenantGuid;
    }
    return payload;
}

export const BRIDGE = bridgeAdapter(bridgeGenerator);
export const API = networkAdapter(apiGenerator);
export const blocklyPrefix = blocklyPrefixFunctions();
export const blocklyPostfix = blocklyPostfixFunctions();
